import React, { useRef, useEffect, forwardRef } from 'react';
import { VariableSizeList } from 'react-window';
import { Box } from '@mui/material';

const LISTBOX_PADDING = 8; // top + bottom padding
const MAX_VISIBLE_ITEMS = 8; // max visible rows
const ITEM_HEIGHT = 36; // base height

const getItemHeight = (item) => {
  // Rough estimate: if item has multi-line content, increase height
  if (!item || !item.props) return ITEM_HEIGHT;
  const children = item.props.children;
  if (Array.isArray(children)) return ITEM_HEIGHT + children.length * 18;
  return ITEM_HEIGHT;
};

const ListboxComponent = forwardRef(({ children, selectedValue, ...other }, ref) => {
  const listRef = useRef();
  const rootRef = useRef(null);
  const rowHeights = useRef({});
  const [containerHeight, setContainerHeight] = React.useState(400); // default

  const itemData = React.Children.toArray(children);

  // Reset list sizes when children change
  useEffect(() => {
    if (listRef.current) listRef.current.resetAfterIndex(0, true);
  }, [children]);

  // Scroll to selected item
  useEffect(() => {
    if (!selectedValue || !listRef.current) return;
    const index = itemData.findIndex((item) => item.props.id === selectedValue);

    if (index >= 0) {
      listRef.current.scrollToItem(index, 'smart');
    }
  }, [selectedValue, itemData]);

  // Measure container height
  useEffect(() => {
    if (rootRef.current) {
      setContainerHeight(rootRef.current.clientHeight);
    }
  }, []);

  const getRowHeight = (index) => rowHeights.current[index] || getItemHeight(itemData[index]);

  const Row = ({ index, style }) => {
    const item = itemData[index];
    const rowRef = useRef(null);

    useEffect(() => {
      if (rowRef.current) {
        const height = rowRef.current.clientHeight;
        if (rowHeights.current[index] !== height) {
          rowHeights.current[index] = height;
          if (listRef.current) listRef.current.resetAfterIndex(index);
        }
      }
    }, [rowRef, index, item]);

    return (
      <div style={{ ...style, top: style.top + LISTBOX_PADDING }}>
        <div ref={rowRef}>{item}</div>
      </div>
    );
  };
  return (
    <div ref={rootRef} {...other} style={{ width: '100%', height: '100%' }}>
      <VariableSizeList
        ref={listRef}
        height={containerHeight}
        itemCount={itemData.length}
        itemSize={getRowHeight}
        width="100%"
        overscanCount={5}
      >
        {Row}
      </VariableSizeList>
    </div>
  );
});

export default ListboxComponent;
